import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { Button, Col, Container, Modal, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import Select from "react-select"
import Icon from "../common/icon/icon.component"
import { GlobalHelper } from "../indexImports"
import FormattedInputField from "../sharedComponents/formattedInputField.component"
import Fee from "../state/fee/Fee"
import FeeService from "../state/fee/FeeService"
import { isError } from "../state/helpers/Error"
import IntlProps from "../state/helpers/IntlProps"
import { SelectOption } from "../state/helpers/SelectOption"


type OwnProps = {
    feeIds: string[],
    mainObject: string,
    invoiceId: string,
    quoteId: string,
    objectId: string,
    pricingTableId: string,
    billingMethod: string,
    onApplyFee: () => void,
}

type feeInput = {
    feeId: string,
    feeRate: string,
    daysAfterDueDate: number,
    pricingFeeCalculationId: string,
}

const FeeModal = (props: OwnProps & IntlProps) => {
    useEffect(() => {
        (async () => {
            const r = await FeeService.get()
            if (!isError(r)) {
                if (props.feeIds && props.feeIds.length > 0) {
                    setFeeInputs(JSON.parse(JSON.stringify(props.feeIds)))
                } else {
                    setFeeInputs([])
                }
                setFees(r as Fee[])
                setLoading(false)
            }
        })()
    }, [props.feeIds])

    const [feeInputs, setFeeInputs] = useState<feeInput[]>([])
    const [fees, setFees] = useState<Fee[]>([])
    const [show, setShow] = useState<boolean>(false)
    const [isLoading, setLoading] = useState<boolean>(true)

    const getSelectedFee = (feeId: string) => fees.find(f => f.getId() === feeId)

    const handleFeeChange = (option, index) => {
        const feeId = option.value
        const list = [...feeInputs]
        const selectedFee = getSelectedFee(feeId)
        list[index].feeId = feeId
        if (selectedFee) {
            list[index].feeRate = selectedFee.getRate()
            list[index].daysAfterDueDate = selectedFee.getDaysAfterDueDate()
        }
        setFeeInputs(list)
    }

    const handleAddFee = (feeId: string) => {
        const selectedFee = getSelectedFee(feeId)
        let selectedFeeId = ""
        let feeRate = ""
        let daysAfterDueDate = 0
        if (selectedFee) {
            selectedFeeId = selectedFee.getId()
            feeRate = selectedFee.getRate()
            daysAfterDueDate = selectedFee.getDaysAfterDueDate()
        }

        const newfield = {
            feeId: selectedFeeId,
            feeRate: feeRate,
            daysAfterDueDate: daysAfterDueDate,
            pricingFeeCalculationId: ""
        }
        setFeeInputs([...feeInputs, newfield])
    }

    const handleRemoveFee = (index) => {
        const list = [...feeInputs]
        list.splice(index, 1)
        setFeeInputs(list)
    }

    const createOptions = (fees: Fee[]): SelectOption[] => {
        if (props.mainObject === "invoices") {
            return fees.map(fee => ({label: fee.getName(), value: fee.getId()}))
        }
        return fees.filter(fee => !fee.isLateFee()).map(fee => ({label: fee.getName(), value: fee.getId()}))
    }

    const displayRate = (fee, index: number) => {
        const selectedFee = fees.find(f => f.getId() === fee.feeId || fee.value)
        const rateType = selectedFee ? selectedFee.getRateType() : ""
        const {inputGroupText} = GlobalHelper.GetLabelAndSign(rateType)
        const isLateFee = selectedFee?.isLateFee()

        let rate = fee.feeRate
        let daysAfterDueDate = fee.daysAfterDueDate
        if (fee.value) {
            rate = selectedFee?.getRate()
            daysAfterDueDate = selectedFee?.getDaysAfterDueDate()
        }

        return selectedFee &&
            <>
                <Col xs={isLateFee ? 2 : 4}>
                    <div className="input-group mb-2">
                        <div className="input-group-prepend">
                            <span className="input-group-text">{inputGroupText}</span>
                        </div>
                        <FormattedInputField
                            name="rate"
                            id={index}
                            type="text"
                            className="form-control"
                            placeholder={GlobalHelper.applyLocaleDecimal("5.5", AppContext.getLocale().getLanguage())}
                            valueType="money"
                            value={rate}
                            onValueChange={(e) => updateTaxRate(e, index, selectedFee ? selectedFee.getId() : "")}
                        />
                    </div>
                </Col>
                {
                    isLateFee &&
                    <Col xs={isLateFee ? 2 : 4}>
                        <div className="input-group mb-2">
                            <div className="input-group-prepend">
                                <span className="input-group-text">days</span>
                            </div>
                            <input
                                type="number"
                                className="form-control"
                                min="1"
                                value={daysAfterDueDate}
                                onChange={(e) => updateDaysAfterDueDate(e, index, selectedFee ? selectedFee.getId() : "")}/>
                        </div>
                    </Col>
                }
            </>
    }

    const updateDaysAfterDueDate = (e: React.ChangeEvent<HTMLInputElement>, index: number, feeId: string) => {
        const data = [...feeInputs]
        data[index].daysAfterDueDate = Number(e.target.value)
        if (feeId) {
            const selectedFee = fees.find(f => f.getId() === feeId)
            if (selectedFee) {
                data[index].feeId = selectedFee.getId()
            }
        }
        setFeeInputs(data)
    }

    const updateTaxRate = (e: string, index: number, feeId: string) => {
        const data = [...feeInputs]
        data[index].feeRate = e
        if (feeId) {
            const selectedFee = getSelectedFee(feeId)
            if (selectedFee) {
                data[index].feeId = selectedFee.getId()
            }
        }
        setFeeInputs(data)
    }

    const applyFee = () => {
        FeeService.applyFee(props, feeInputs).then(() => {
            handleClose()
            props.onApplyFee()
        })
    }

    const reInitalizeSelect = () => {
        if ((JSON.parse(JSON.stringify(props.feeIds))).length === 0) {
            setFeeInputs([])
        } else {
            setFeeInputs(JSON.parse(JSON.stringify(props.feeIds)))
        }
    }

    const handleClose = () => {
        setShow(false)
        setTimeout(() => {
            reInitalizeSelect()
        }, 500)

    }
    const handleShow = () => setShow(true)

    const isFeeArchived = (feeId: string): boolean => {
        return fees.filter(fee => fee.getId() === feeId).length === 0
    }

    const options = [...createOptions(fees)]
    const getAddFeeButton = (): JSX.Element => {
        if (options && options.length > 0) {
            return <small>
                <Button variant="primary"
                    className="mr-2 ml-3"
                    onClick={() => handleAddFee(options[0].value as string)}
                    style={{height: "38px"}}>
                    <Icon value={"plus-circle"} className={"pr-1"}/>
                    <FormattedMessage
                        id="quote.fee_modal.add_a_fee"
                        defaultMessage="Add a fee"
                    />
                </Button>
            </small>
        }
        return <p className="text-align-center mt-2">
            <FormattedMessage
                id="quote.fee_modal.no_fees_to_add_message"
                defaultMessage="You currently have no fees"
            />
        </p>
    }

    return <>
        <a onClick={handleShow} className="btn btn-sm mx-1 btn-outline-primary">
            <FormattedMessage
                id="quote.edit.fee_quote_btn"
                defaultMessage="Fee"
            />
        </a>

        {
            !isLoading && <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FormattedMessage
                            id="quote.fee_modal.title"
                            defaultMessage="Fees"
                        />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {feeInputs.map((fee, index) => (
                        <Container key={index}>
                            <Row>
                                <Col xs={4}>
                                    <Select
                                        style={{paddingBottom: "2px"}}
                                        className="mb-2"
                                        name="feeSelect"
                                        value={isFeeArchived(fee.feeId) && options.length>0? options[0].value : fee.feeId}
                                        onChange={option => handleFeeChange(option, index)}
                                        options={options}
                                        clearable={false}
                                    />
                                </Col>
                                {displayRate(fee.feeId ? fee : options[0], index)}
                                <Col>
                                    {
                                        <small>
                                            <Button variant="danger" className="mr-2" style={{height: "38px"}}
                                                onClick={() =>
                                                    handleRemoveFee(index)}>
                                                <Icon value={"trash"} className={"pr-1"}/>
                                            </Button>
                                        </small>

                                    }
                                </Col>
                            </Row>
                            <Row>
                                {feeInputs.length - 1 === index &&
                                    getAddFeeButton()
                                }
                            </Row>
                        </Container>
                    ))}
                    {feeInputs.length === 0 &&
                        getAddFeeButton()
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        <FormattedMessage
                            id="quote.fee_modal.close_btn"
                            defaultMessage="Close"
                        />
                    </Button>
                    <Button variant="primary" onClick={() => applyFee()}>
                        <FormattedMessage
                            id="quote.fee_modal.save_btn"
                            defaultMessage="Save"
                        />
                    </Button>
                </Modal.Footer>
            </Modal>
        }
    </>
}
export default injectIntl(FeeModal)



// WEBPACK FOOTER //
// ./src/quote/FeeModal.tsx